<template>
  <div class="ventrata-calendar-widget-container">
    <div v-show="!isCurrencyConflict">
      <VentrataCalendar v-if="initAvailabilityList" :product="product" @selectDate="onDateChange" />
      <VentrataTimeSelector v-if="selectedDate" :currency="currency" :product="product" :selected-date="selectedDate" @selectTime="onTimeSelect" />
      <VentrataTicketSelector v-if="selectedTimeSlot" :currency="currency" :product="product" :selected-time="selectedTimeSlot" :current-selected-units="selectedUnits" @updateUnit="onUnitChange" @autoAddWarning="onAutoAddWarning" @cancelAutoWarning="onCancelAutoWarning" />

      <div v-if="selectedUnits && selectedUnits.length" class="total-price-container">
        <div class="total-price-title">
          Total Price
        </div>
        <div class="total-price-value">
          {{ totalPrice }}
        </div>
      </div>

      <div v-if="initAvailabilityList && showAddToCartBtn" class="add-cart-container">
        <button :disabled="!validBooking || submitting" :class="{ disabled: !validBooking || submitting }" @click="addToCart">
          {{ addToCartBtnText }}
        </button>
        <div v-if="cartComputedErrorMsg" class="cart-err">{{ cartComputedErrorMsg }}</div>
        <div v-if="!cartComputedErrorMsg && displayWarning" class="cart-err">Selected tickets should be accompanied by at least one adult.</div>
        <div class="free-cancellation">
          <div class="free-cancellation-icon"></div>
          <span class="free-cancellation-text">Free Cancellation</span>
          <span v-if="onCancellationRemindComputed" class="add-tooltip-container">
            <InfoToolTip
              :id="'price-tooltip'"
              class="add-tooltip-icon"
              :text="onCancellationRemindComputed"
              :custom-style="{ height: '18px', weight: '18px', stroke: '#889BAA' }"
              :variant="'primary'"
            />
          </span>
        </div>
        <LoadingBar :loading="loading" class="ventrata-calendar-loading" />
      </div>

      <div
        class="ventrata-potential-booking-conflict"
        v-if="hasConflict && validBooking && !procced"
      >
        <VentrataPotentialBookingConflict
          @back="onBack"
          @proceed="onProceed"
          :selected-available-time="selectedTimeSlot"
        />
      </div>
    </div>

    <div v-show="isCurrencyConflict">
      <VentrataCurrencyConflict
        @proceed="onProceedCurrency"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { formatDate, addLeadingZeros } from '@/helpers/main'
import { addDetailedCurrencySymbol } from '@/helpers/price'
import { getAvailabilityPayload, validateTicketTypes } from '@/helpers/ventrata'

import VentrataCalendar from '@/components/utils/ventrata/VentrataCalendar'
import VentrataTimeSelector from '@/components/utils/ventrata/VentrataTimeSelector'
import VentrataTicketSelector from '@/components/utils/ventrata/VentrataTicketSelector'
import InfoToolTip from '@/components/utils/InfoToolTip'
import LoadingBar from '@/components/utils/LoadingBar'
import VentrataCurrencyConflict from '@/components/utils/ventrata/VentrataCurrencyConflict'
import VentrataPotentialBookingConflict from '@/components/utils/ventrata/VentrataPotentialBookingConflict'
import { setCsCookie } from '../../../helpers/cookie'

const generateUnitList = (data) => {
  const { unitId, pax } = data
  return [...Array(pax)].map(() => ({ unitId, extraItems: [] }))
}

export default {
  name: 'VentrataBookingWidget',
  components: {
    LoadingBar,
    VentrataCalendar,
    VentrataTimeSelector,
    InfoToolTip,
    VentrataTicketSelector,
    VentrataCurrencyConflict,
    VentrataPotentialBookingConflict
  },
  props: {
    product: {
      type: Object,
      require: true
    },
    fromPrice: {
      type: Number,
      require: true
    }
  },
  data () {
    return {
      checkoutConfig: null,
      selectedDate: null,
      selectedTimeSlot: null,
      selectedUnits: null,
      addToCartBtnText: 'Add to Cart',
      displayWarning: false,
      warningCleanupTimer: null,
      loading: false,
      isEnoughPax: false,
      procced: false
    }
  },
  computed: {
    ...mapGetters(['tour', 'currency']),
    ...mapGetters({ 
      initAvailabilityList: 'ventrata/initAvailabilities',
      cart: 'ventrata/cart' 
    }),
    orderID () {
      return this.cart?.id
    },
    isCurrencyConflict () {
      return this.currency !== this.cartCurrency
    },
    cartCurrency () {
      if (!this.cart) {
        return this.currency
      }

      if (!this.cart.bookings) {
        return this.currency
      }

      if (!this.cart.bookings.length) {
        return this.currency
      }

      return this.cart.bookings.map((booking) => booking.pricing.currency)[0]
    },
    productUnits () {
      return this.product?.options?.[0].units ?? []
    },
    cartComputedErrorMsg () {
      const isTicketTypeValid = validateTicketTypes(this.selectedUnits, this.productUnits)
      if (!isTicketTypeValid) {
        return 'Selected tickets should be accompanied by at least one adult.'
      }
      return ''
    },
    showAddToCartBtn () {
      if (!this.hasConflict) {
        return true
      }

      if (!this.validBooking) {
        return true
      }

      if (this.procced) {
        return true
      }

      return false
    },
    hasConflict () {
      if (!this.selectedTimeSlot) {
        return false
      }

      const { localDateTimeStart, localDateTimeEnd } = this.selectedTimeSlot
      if (!localDateTimeStart || !localDateTimeEnd) {
        return false
      }

      if (!this.cart) {
        return false
      }

      const bookings = this.cart.bookings ? this.cart.bookings : []
      if (bookings.length === 0) {
        return false
      }

      const conflictBooking = bookings.filter((booking) => {
        const { localDateTimeStart: currentBookingStart, localDateTimeEnd: currentBookingEnd } = booking.availability
        return (
          (localDateTimeStart >= currentBookingStart && localDateTimeStart <= currentBookingEnd) ||
          (localDateTimeEnd >= currentBookingStart && localDateTimeEnd <= currentBookingEnd)
        )
      })

      return conflictBooking.length > 0 ? true : false
    },
    submitting () {
      return this.addToCartBtnText === 'adding...'
    },
    validBooking () {
      // Temporarily allow all bookings
      const unitLength = this.selectedUnits?.reduce((acc, unit) => {
        return acc + unit.pax
      }, 0)
      const isTicketTypeValid = validateTicketTypes(this.selectedUnits, this.productUnits)
      return this.selectedTimeSlot && unitLength > 0 && this.isEnoughPax && isTicketTypeValid
    },
    totalPrice () {
      return addDetailedCurrencySymbol(this.currency, (this.selectedUnits?.reduce((acc, unit) => {
        return acc + unit.original * unit.pax
      }, 0) / 100).toFixed(2))
    },
    onCancellationRemindComputed () {
      const option = (this.product?.options || [])[0]
      const cancellationCutoffAmount = option?.cancellationCutoffAmount
      if (cancellationCutoffAmount) {
        return `Your booking can be cancelled and refunded for free up to ${ option.cancellationCutoff } in advance of the tour date & time.`
      } else {
        return 'Free Cancellation before tour date'
      }
    }
  },
  methods: {
    onDateChange (date) {
      // date could be 2025-2-19
      const validDate = addLeadingZeros(date)
      this.selectedDate = formatDate(validDate, 'YYYY-MM-DD')
    },
    onProceedCurrency () {
      // $('.currency-select-value').text(this.cartCurrency + ' ' + symbol)
      this.$store.commit('setCurrency', this.cartCurrency)
    },
    onTimeSelect (timeSlot) {
      if (!timeSlot.vacancies) {
        this.selectedTimeSlot = null
        this.selectedUnits = []
        return
      }
      this.selectedTimeSlot = timeSlot
    },
    onAutoAddWarning () {
      this.displayWarning = true
      const _this = this
      this.warningCleanupTimer = setTimeout(() => {
        _this.displayWarning = false
      }, 5000)
    },
    onCancelAutoWarning () {
      this.displayWarning = false
    },
    onUnitChange (data) {
      this.selectedUnits = data
      this.onCheckAvailability()
    },
    async onCheckAvailability () {
      // TODO: availability check
      const paxTotalCount = this.selectedUnits?.reduce((acc, unit) => {
        return acc + unit.pax
      }, 0)
      const payload = getAvailabilityPayload(this.product, this.selectedDate, this.selectedDate, this.currency)
      this.loading = true
      try {
        const ret = await this.$store.dispatch('ventrata/getAvailability', payload)
        const availability = ret.find(item => item.id === this.selectedTimeSlot.id)
        if (!availability || availability.vacancies < paxTotalCount) {
          this.isEnoughPax = false
        } else {
          this.isEnoughPax = true
        }
      } catch (error) {
        this.isEnoughPax = false
      } finally {
        this.loading = false
      }
    },
    onProceed () {
      this.procced = true
    },
    onBack () {
      this.procced = false
      this.selectedUnits = []
    },
    async addToCart () {
      this.addToCartBtnText = 'adding...'
      try {
        // need to re-load "cart" before add new booking to cart
        await this.$store.dispatch('ventrata/initCart')
        // console.log('cart re-init..')
        const unitItems = this.selectedUnits.map((unit) => {
          return generateUnitList(unit)
        })

        const csCity = this.tour?.cityName
        const csTourDate = this.selectedDate
        const csProductName = this.tour?.shortTitle
        const csQuantity = this.selectedUnits.map((unit) => ({ type: unit.unitType, pax: unit.pax }))

        setCsCookie({ csCity, csTourDate, csProductName, csQuantity })

        const firstGalleryImage = this.tour?.gallery?.[0]
        const newBooking = {
          optionId: 'DEFAULT',
          productId: this.product.id,
          unitItems: unitItems.flat(),
          extraItems: [],
          availabilityId: this.selectedTimeSlot.id,
          resourceAllocations: [],
          metadata: {
            site: 'walksofitaly',
            citySlug: this.tour?.citySlug,
            slug: this.tour?.tourSlug,
            reviewStatus: this.tour?.reviewStatus,
            title: this.tour?.title,
            propertyId: this.tour?.propertyId,
            bookingTypeId: this.tour?.bookingTypeId,
            shortTitle: this.tour?.shortTitle,
            listingImage: firstGalleryImage?.url,
            listingImageAlt: firstGalleryImage?.title,
          }
        }

        // console.log('newBooking :>> ', newBooking)
        if (!this.orderID) {
          const newOrder = await this.$store.dispatch('ventrata/createOrder', {
            bookings: [newBooking],
            currency: this.currency
            // currency: 'USD'
          })
          if (newOrder.error) {
            // TODO display error message
            throw new Error('Failed to create order, please try again later.')
          }
          // updateCart
          await this.$store.dispatch('ventrata/updateCart', newOrder)
          const bookingId = newOrder.bookings[0].id
          this.$emit('addToCart', true, bookingId)
          this.utagAddToCart(this.tour, newOrder.bookings?.[0])
        } else {
          // console.log('start reserving...')
          const resp = await this.$store.dispatch('ventrata/reserveBooking', { ...newBooking, orderId: this.orderID })
          if (resp.error) {
            // TODO display error message
            throw new Error('Failed to reserve booking, please try again later.')
          }
          this.utagAddToCart(this.tour, resp)
          const updatedOrder = await this.$store.dispatch('ventrata/getOrder', this.orderID)
          const currentBookingId = resp.id
          this.$emit('addToCart', true, currentBookingId, updatedOrder)
        }
      } catch (error) {
        this.addToCartBtnText = 'Add to Cart'
        console.log('error :>> ', error)
        alert(error.message || error)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.ventrata-calendar-widget-container {
  button {
    width: 100%;
    margin-top: 8px;
    background-color: $woi-gold-main;
    border: none;
    border-radius: 20px;
    padding: 8px 16px;
    color: #fff;
    box-shadow: none;

    &.disabled {
      color: #939393;
      background-color: #D9D9D9;
    }
    &:not(.disabled):hover {
      cursor: pointer;
    }
  }

  .total-price-container, .add-cart-container {
    display: flex;
    justify-content: space-between;
    margin-top: 16px;
    padding: 32px;
    border-radius: 4px;
    background-color: $text-white;
    cursor: pointer;
  }

  .ventrata-potential-booking-conflict {
    margin-top: 16px;
  }

  .total-price-container {
    align-items: center;
    font-size: 28px;
    line-height: 32px;
    @include font__header-5();
    .total-price-title {

    }

    .total-price-value {

    }

    p {
      margin: 0;
      font-weight: bold;
    }
  }

  .add-cart-container {
    position: relative;
    flex-direction: column;

    .ventrata-calendar-loading {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
    }

    button {
      height: 48px;
      border-radius: 0;
      margin: 0;
      @include font__subtitle-1--regular();
      text-align: center;
      text-transform: uppercase;
    }
    .cart-err {
      color: $brand-purple;
    }
    .free-cancellation {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      padding-top: 16px;
      .free-cancellation-icon {
        display: inline-block;
        background: $woi-gold-main;
        mask-image: url('@/assets/svg/check-circle.svg');
        mask-size: cover;
        width: 24px;
        height: 24px;
      }

      .free-cancellation-text {
        vertical-align: middle;
        margin-left: 4px;
        color: $woi-gold-main;
        @include font__small();
      }

      .add-tooltip-container {
        position: relative;
        margin-left: 8px;
        .price-tooltip {
          padding-left: 4px;
          font-size: 22px;
        }
        .add-tooltip-icon {

        }
      }
    }
  }
}

@include max-width($mobile) {
  .ventrata-calendar-widget-container {
    .add-cart-container {
      padding: 16px 8px;
      button {
        @include font__emphasize--medium();
        text-align: center;
      }
    }
    .total-price-container {
      padding: 16px;
      .total-price-title {

      }
    }
  }
}
</style>

<style lang="scss">
@include max-width($mobile) {
  .add-tooltip-container {
    .add-tooltip-icon {
      .tooltip-wrapper {
        .custom-tooltip {
          &.custom-tooltip-top {
            left: -80px;
          }
        }
      }
    }
  }
}
</style>